import { createSlice } from '@reduxjs/toolkit';
import { Dispatch } from 'redux';
import { mergeInPersistent } from '../../../store/utils';

import { GlobalState } from '../../../types.global';

import { fetchSurvey } from './thunks';
import { selectIsEligible } from './selectors';

import { SurveysState, FINISHED_ACTION } from './types';
import { browserStorageUpdate } from '../../../store/actions';

const INITIAL_STATE: SurveysState = {
  isAgeConfirmed: false,

  // That could be taken out to the toolkit query
  data: {
    url: null,
    list: [],
    fetchedAt: null
  },
  visibility: {
    updatedAtTabs: null,
    updatedAtDate: null,
    updateType: null
  },
};

const MINUTE_IN_MS = 1000 * 60;
const DAY_IN_MS = MINUTE_IN_MS * 60 * 24;

export const getInitialSurveysState = ({
                                         surveys = INITIAL_STATE,
                                         user,
                                         ...state
                                       }: GlobalState): SurveysState => {
  const newState = mergeInPersistent(INITIAL_STATE, surveys || {});

  const now = Date.now();
  if (!selectIsEligible({ ...state, user, surveys: newState })) {
    return newState;
  }

  const { visibility, data } = newState;

  // if (newState.visibility.updateType === 'FETCHED' && (now - data.fetchedAt) > (MINUTE_IN_MS * 5)) {
  if (
    (newState.visibility.updateType === 'FETCHED' && (now - data.fetchedAt) > DAY_IN_MS) ||
    (newState.visibility.updateType === 'FINISHED')
  ) {
    newState.data = INITIAL_STATE.data;
    newState.visibility.updateType = 'FETCH';
  }

  if (['FETCH', 'FETCHED'].includes(visibility.updateType)) {

    // if (user.ntpVisitCounter - visibility.updatedAtTabs > 5) {
    //   visibility.updatedAtTabs = user.ntpVisitCounter;
    //   visibility.updatedAtDate = now;
    //   visibility.updateType = 'HIDDEN';
    // }
  } else {
    if (
      !visibility.updateType ||
      (visibility.updateType === 'HIDDEN' &&
        now - visibility.updatedAtDate > DAY_IN_MS * 2) ||
      (visibility.updateType === 'FINISHED' &&
        user.ntpVisitCounter - visibility.updatedAtTabs > 10)
    ) {
      visibility.updatedAtTabs = user.ntpVisitCounter;
      visibility.updatedAtDate = now;
      visibility.updateType = 'FETCH';
    }
  }

  return newState;
};

export const surveysSlice = createSlice({
  name: 'surveys',
  initialState: INITIAL_STATE,
  reducers: {
    finishedOnboarding: (state): void => {
      state.isAgeConfirmed = true;
    },
    surveyHidden: (state, { payload }): void => {
      state.visibility.updatedAtTabs = payload.ntpVisitCounter;
      state.visibility.updatedAtDate = payload.now;
      state.visibility.updateType = payload.isForever ? 'HIDDEN_FOREVER' : 'HIDDEN';

      state.data = INITIAL_STATE.data;
    },
    surveyFinished: (state, { payload }): void => {
      state.visibility.updatedAtTabs = payload.ntpVisitCounter;
      state.visibility.updatedAtDate = payload.now;
      state.visibility.updateType = 'FINISHED';

      state.data.list.shift();
      // if used all, the next time re-fetch
      if (!state.data.list.length) {
        state.data = {
          ...INITIAL_STATE.data,
          url: state.data.url,
        };
      }
    }
  },
  extraReducers: (builder) => {
    builder.addCase(browserStorageUpdate, (state, { payload }) => {
      if (payload.isNewer) {
        return getInitialSurveysState(payload.state);
      }
    });

    builder.addCase(fetchSurvey.fulfilled, (state, { payload }) => {
      // if (payload === null) return;

      state.data = {
        url: payload.url,
        list: payload.list,
        fetchedAt: payload.fetchedAt,
      };

      state.visibility.updateType = 'FETCHED';
      // In case someone needs to re-fetch because of 5min we don't want to reset counters
      // state.visibility.updatedAtTabs = payload.ntpVisitCounter;
      // state.visibility.updatedAtDate = payload.fetchedAt;
    });

    builder.addCase(fetchSurvey.rejected, (state) => {
      state.visibility.updateType = 'FETCHED';
      state.data = INITIAL_STATE.data;
    });

    builder.addCase(fetchSurvey.pending, (state) => {
      state.data = INITIAL_STATE.data;
    });
  }
});

export const { finishedOnboarding } = surveysSlice.actions;

export const surveyHidden =
  (isForever: boolean) =>
    (dispatch: Dispatch, getState: () => GlobalState): void => {
      const state = getState();

      dispatch(
        surveysSlice.actions.surveyHidden({
          isForever,
          now: Date.now(),
          ntpVisitCounter: state.user.ntpVisitCounter
        })
      );
    };

export const surveyFinished = (action: FINISHED_ACTION) =>
  (dispatch: Dispatch, getState: () => GlobalState): void => {
    const state = getState();
    dispatch(
      surveysSlice.actions.surveyFinished({
        action,
        ntpVisitCounter: state.user.ntpVisitCounter,
        now: Date.now()
      })
    );
  };

export default surveysSlice.reducer;
